import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import ptBR from 'date-fns/locale/pt-BR';
import Select, { components } from 'react-select';
import toast, { Toaster } from 'react-hot-toast';
import { FileText, Printer, Search, Trash2 } from 'lucide-react';
import 'react-datepicker/dist/react-datepicker.css';
import './ProducaoBPA.css';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/apiService';

// Registrar o locale português brasileiro
registerLocale('pt-BR', ptBR);
setDefaultLocale('pt-BR');

const truncarLabel = (texto, max = 48) => {
  const valor = String(texto || '');
  if (valor.length <= max) return valor;
  return `${valor.slice(0, max).trim()}...`;
};

/** Resumo compacto no multi-select (altura fixa); detalhes ao abrir o menu */
const ProcedimentoMultiValue = (props) => {
  const values = props.getValue() || [];
  if (props.index !== 0) return null;

  const titulo = values.map((item) => item.label).join('\n');
  const texto =
    values.length === 1
      ? truncarLabel(values[0].label, 56)
      : `${values.length} procedimentos selecionados...`;

  return (
    <div className="procedimento-multi-resumo" title={titulo}>
      {texto}
    </div>
  );
};

/** Cabeçalho do menu: selecionar / desmarcar todos */
const ProcedimentoMenuList = (props) => {
  const { children, getValue, setValue, selectProps } = props;
  const opcoes = Array.isArray(selectProps.options) ? selectProps.options : [];
  const selecionados = getValue() || [];
  const todosMarcados = opcoes.length > 0 && selecionados.length >= opcoes.length;

  const handleToggleTodos = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (todosMarcados) {
      setValue([], 'deselect-option');
    } else {
      setValue(opcoes, 'select-option');
    }
  };

  return (
    <components.MenuList {...props}>
      {opcoes.length > 0 && (
        <div
          className="procedimento-select-all"
          role="option"
          aria-selected={todosMarcados}
          onMouseDown={handleToggleTodos}
        >
          {todosMarcados ? 'Desmarcar todos' : `Selecionar todos (${opcoes.length})`}
        </div>
      )}
      {children}
    </components.MenuList>
  );
};

const procedimentoSelectComponents = {
  MultiValue: ProcedimentoMultiValue,
  MultiValueRemove: () => null,
  MenuList: ProcedimentoMenuList
};

const ProducaoBPA = () => {
  const { token } = useAuth();
  // Calcular datas do mês anterior ao atual
  const getPreviousMonthDates = () => {
    const hoje = new Date();
    const mesAnterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
    const ultimoDiaMesAnterior = new Date(hoje.getFullYear(), hoje.getMonth(), 0);
    
    return {
      dataInicio: mesAnterior,
      dataFim: ultimoDiaMesAnterior
    };
  };

  const [filtros, setFiltros] = useState({
    unidade: null,
    profissional: null,
    procedimento: [],
    categoria: null,
    dataInicio: getPreviousMonthDates().dataInicio,
    dataFim: getPreviousMonthDates().dataFim,
    tipoAtendimento: 'ambulatorio',
    mostrarDadosBPA: false // Novo filtro para mostrar exatamente os dados que vão para o BPA
  });

  // Estado para controlar campos com erro (bordas vermelhas)
  const [camposComErro, setCamposComErro] = useState({
    unidade: false,
    dataInicio: false,
    dataFim: false
  });

  // Estado para controlar se já mostrou toast (evitar duplicatas)
  const [ultimoToastMostrado, setUltimoToastMostrado] = useState(null);


  // Estados para os dados dos selects
  const [opcoesUnidade, setOpcoesUnidade] = useState([]);
  const [opcoesProfissional, setOpcoesProfissional] = useState([]);
  const [opcoesProcedimento, setOpcoesProcedimento] = useState([]);
  const [opcoesCategoria, setOpcoesCategoria] = useState([]);
  const [loading, setLoading] = useState({
    unidade: false,
    profissional: false,
    procedimento: false,
    categoria: false
  });

  // Estados para os dados da tabela
  const [dadosTabela, setDadosTabela] = useState([]);
  const [loadingTabela, setLoadingTabela] = useState(false);
  const [loadingImprimir, setLoadingImprimir] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [procedimentosDisponiveis, setProcedimentosDisponiveis] = useState([]);
  const [procedimentosSelecionados, setProcedimentosSelecionados] = useState([]);
  const [loadingProcedimentos, setLoadingProcedimentos] = useState(false);

  // Estados para paginação
  const [paginacao, setPaginacao] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  // Função para buscar dados da API
  const fetchDataFromAPI = async (endpoint, setData, setLoadingState) => {
    if (!token) {
      console.error('Token de autenticação não encontrado');
      return;
    }

    try {
      setLoadingState(true);
      const result = await apiService.authenticatedRequest(`/api/queries/${endpoint}`, {
        method: 'GET'
      });

      if (result.success && result.data) {
        setData(result.data);
      } else {
        console.error(`Erro na resposta da API para ${endpoint}:`, result.error);
      }
    } catch (error) {
      console.error(`Erro ao buscar ${endpoint}:`, error);
    } finally {
      setLoadingState(false);
    }
  };

  // Carregar dados dos selects ao montar o componente
  useEffect(() => {
    if (token) {
      fetchDataFromAPI('unidades', setOpcoesUnidade, (loading) => setLoading(prev => ({ ...prev, unidade: loading })));
      fetchDataFromAPI('profissionais', setOpcoesProfissional, (loading) => setLoading(prev => ({ ...prev, profissional: loading })));
      fetchDataFromAPI('procedimentos', setOpcoesProcedimento, (loading) => setLoading(prev => ({ ...prev, procedimento: loading })));
      fetchDataFromAPI('categorias', setOpcoesCategoria, (loading) => setLoading(prev => ({ ...prev, categoria: loading })));
    }
  }, [token]);

  const handleInputChange = (field, value) => {
    setFiltros(prev => ({
      ...prev,
      [field]: value
    }));

    // Select múltiplo de procedimentos: sincroniza seleção do BPA
    if (field === 'procedimento') {
      const options = Array.isArray(value) ? value : [];
      setProcedimentosSelecionados(
        options
          .map((opt) => String(opt?.value || '').replace(/\D/g, ''))
          .filter((codigo) => codigo.length === 10)
      );
    }
    
    // Remover erro do campo quando o usuário preencher
    if (camposComErro[field]) {
      setCamposComErro(prev => ({
        ...prev,
        [field]: false
      }));
    }
  };

  const handleTipoAtendimento = (tipo) => {
    setFiltros(prev => ({
      ...prev,
      tipoAtendimento: tipo
    }));
  };

  // Função para formatar data no formato brasileiro (DD/MM/AAAA)
  const formatarDataBR = (dataString) => {
    if (!dataString || dataString === 'N/A') return 'N/A';
    
    try {
      // Se já está no formato YYYY-MM-DD, converter para DD/MM/YYYY
      if (dataString.includes('-')) {
        const [ano, mes, dia] = dataString.split('-');
        return `${dia}/${mes}/${ano}`;
      }
      
      // Se está no formato YYYYMMDD, converter para DD/MM/YYYY
      if (dataString.length === 8 && /^\d{8}$/.test(dataString)) {
        const ano = dataString.substring(0, 4);
        const mes = dataString.substring(4, 6);
        const dia = dataString.substring(6, 8);
        return `${dia}/${mes}/${ano}`;
      }
      
      return dataString;
    } catch (error) {
      console.warn('Erro ao formatar data:', error);
      return dataString;
    }
  };

  // Função para sanitizar filtros (remover referências circulares)
  const sanitizarFiltros = (filtros) => {
    // Criar objetos completamente novos para evitar qualquer referência circular
    const filtrosLimpos = {
      unidade: null,
      profissional: null,
      procedimento: null,
      categoria: null,
      dataInicio: '',
      dataFim: '',
      tipoAtendimento: 'ambulatorio',
      mostrarDadosBPA: false,
      procedimentosCodigos: []
    };
    
    try {
      // Processar unidade
      if (filtros.unidade && typeof filtros.unidade === 'object' && filtros.unidade !== null) {
        try {
          const unidadeValue = filtros.unidade.value;
          const unidadeLabel = filtros.unidade.label;
          if (unidadeValue !== undefined && unidadeValue !== null && 
              unidadeLabel !== undefined && unidadeLabel !== null) {
            filtrosLimpos.unidade = {
              value: String(unidadeValue),
              label: String(unidadeLabel)
            };
          }
        } catch (e) {
          console.warn('Erro ao processar unidade:', e);
        }
      }
      
      // Processar profissional
      if (filtros.profissional && typeof filtros.profissional === 'object' && filtros.profissional !== null) {
        try {
          const profissionalValue = filtros.profissional.value;
          const profissionalLabel = filtros.profissional.label;
          if (profissionalValue !== undefined && profissionalValue !== null && 
              profissionalLabel !== undefined && profissionalLabel !== null) {
            filtrosLimpos.profissional = {
              value: String(profissionalValue),
              label: String(profissionalLabel)
            };
          }
        } catch (e) {
          console.warn('Erro ao processar profissional:', e);
        }
      }
      
      // Processar procedimento (select múltiplo: array de { value, label } com código SIGTAP)
      if (Array.isArray(filtros.procedimento) && filtros.procedimento.length > 0) {
        const codigos = [];
        const procedimentos = [];
        for (const item of filtros.procedimento) {
          if (item && item.value != null) {
            const codigo = String(item.value).replace(/\D/g, '');
            if (codigo.length === 10) {
              codigos.push(codigo);
              procedimentos.push({
                value: codigo,
                label: String(item.label || codigo)
              });
            }
          }
        }
        if (procedimentos.length > 0) {
          filtrosLimpos.procedimento = procedimentos;
          filtrosLimpos.procedimentosCodigos = codigos;
        }
      }
      
      // Processar categoria
      if (filtros.categoria && typeof filtros.categoria === 'object' && filtros.categoria !== null) {
        try {
          const categoriaValue = filtros.categoria.value;
          const categoriaLabel = filtros.categoria.label;
          if (categoriaValue !== undefined && categoriaValue !== null && 
              categoriaLabel !== undefined && categoriaLabel !== null) {
            filtrosLimpos.categoria = {
              value: String(categoriaValue),
              label: String(categoriaLabel)
            };
          }
        } catch (e) {
          console.warn('Erro ao processar categoria:', e);
        }
      }
      
      // Processar dataInicio
      if (filtros.dataInicio instanceof Date) {
        filtrosLimpos.dataInicio = filtros.dataInicio.toISOString();
      } else if (typeof filtros.dataInicio === 'string') {
        filtrosLimpos.dataInicio = filtros.dataInicio;
      }
      
      // Processar dataFim
      if (filtros.dataFim instanceof Date) {
        filtrosLimpos.dataFim = filtros.dataFim.toISOString();
      } else if (typeof filtros.dataFim === 'string') {
        filtrosLimpos.dataFim = filtros.dataFim;
      }
      
      // Processar tipoAtendimento
      if (typeof filtros.tipoAtendimento === 'string') {
        filtrosLimpos.tipoAtendimento = filtros.tipoAtendimento;
      }
      
      // Processar mostrarDadosBPA
      if (typeof filtros.mostrarDadosBPA === 'boolean') {
        filtrosLimpos.mostrarDadosBPA = filtros.mostrarDadosBPA;
      }
      
    } catch (error) {
      console.error('Erro ao sanitizar filtros:', error);
      // Retornar objeto básico em caso de erro
      return {
        unidade: null,
        profissional: null,
        procedimento: null,
        categoria: null,
        dataInicio: new Date().toISOString(),
        dataFim: new Date().toISOString(),
        tipoAtendimento: 'ambulatorio',
        mostrarDadosBPA: false
      };
    }
    
    return filtrosLimpos;
  };

  const handleBuscar = async (page = 1) => {
    if (!token) {
      console.error('Token de autenticação não encontrado');
      return;
    }

    // Resetar erros
    const erros = {
      unidade: false,
      dataInicio: false,
      dataFim: false
    };

    let temErro = false;
    const camposObrigatorios = [];

    // Validar campo obrigatório: Unidade
    if (!filtros.unidade || !filtros.unidade.value) {
      erros.unidade = true;
      temErro = true;
      camposObrigatorios.push('Unidade');
    }

    // Validar datas obrigatórias
    if (!filtros.dataInicio) {
      erros.dataInicio = true;
      temErro = true;
      camposObrigatorios.push('Data Início');
    }

    if (!filtros.dataFim) {
      erros.dataFim = true;
      temErro = true;
      camposObrigatorios.push('Data Fim');
    }

    // Se houver erros, mostrar toast e marcar campos
    if (temErro) {
      setCamposComErro(erros);
      toast.error(`⚠️ Campos obrigatórios não preenchidos: ${camposObrigatorios.join(', ')}`);
      return;
    }

    // Limpar marcações de erro
    setCamposComErro(erros);
    setLoadingTabela(true);
    
    try {
      const filtrosSanitizados = sanitizarFiltros(filtros);
      
      const filtrosComPaginacao = {
        ...filtrosSanitizados,
        page: Number(page),
        limit: Number(paginacao.limit)
      };

      // Verificar se o objeto pode ser serializado
      let jsonBody;
      try {
        jsonBody = JSON.stringify(filtrosComPaginacao);
      } catch (jsonError) {
        console.error('Erro ao serializar filtros:', jsonError);
        alert('Erro interno: não foi possível processar os filtros. Tente novamente.');
        return;
      }

      const result = await apiService.authenticatedRequest('/api/bpa/dados-producao', {
        method: 'POST',
        body: jsonBody
      });

      if (result.success) {
        setDadosTabela(result.data);
        setTotalRegistros(result.pagination.total);
        setPaginacao(result.pagination);

        // Na primeira página (nova busca), recarregar códigos distintos
        if (Number(page) === 1) {
          setLoadingProcedimentos(true);
          try {
            const distintos = await apiService.authenticatedRequest('/api/bpa/procedimentos-distintos', {
              method: 'POST',
              body: JSON.stringify(filtrosSanitizados)
            });
            if (distintos.success) {
              const lista = distintos.data || [];
              setProcedimentosDisponiveis(lista);
              // Mantém seleção do filtro multi
              const selecionadosFiltro = Array.isArray(filtros.procedimento)
                ? filtros.procedimento
                    .map((o) => String(o.value || '').replace(/\D/g, ''))
                    .filter((c) => c.length === 10)
                : [];
              if (selecionadosFiltro.length > 0) {
                setProcedimentosSelecionados(selecionadosFiltro);
              } else {
                setProcedimentosSelecionados([]);
              }
            } else {
              setProcedimentosDisponiveis([]);
              toast.error(`❌ Erro ao carregar procedimentos: ${distintos.error}`);
            }
          } catch (distintosError) {
            console.error('Erro ao buscar procedimentos distintos:', distintosError);
            toast.error('❌ Erro ao carregar lista de procedimentos.');
          } finally {
            setLoadingProcedimentos(false);
          }
        }
        
        // Mostrar toast apenas na primeira página
        if (result.pagination.page === 1) {
          if (result.data.length === 0 && ultimoToastMostrado !== 'vazio') {
            setUltimoToastMostrado('vazio');
            toast('ℹ️ Nenhum registro encontrado para os filtros selecionados.');
            // Resetar após 1 segundo
            setTimeout(() => setUltimoToastMostrado(null), 1000);
          } else if (result.data.length > 0) {
            setUltimoToastMostrado('sucesso');
            toast.success(`✅ ${result.pagination.total} registro${result.pagination.total !== 1 ? 's' : ''} encontrado${result.pagination.total !== 1 ? 's' : ''}!`);
            // Resetar após 1 segundo
            setTimeout(() => setUltimoToastMostrado(null), 1000);
          }
        }
      } else {
        setDadosTabela([]);
        setTotalRegistros(0);
        setProcedimentosDisponiveis([]);
        setProcedimentosSelecionados([]);
        setPaginacao({
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
          hasNext: false,
          hasPrev: false
        });
        toast.error(`❌ Erro ao buscar dados: ${result.error}`);
      }
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      setDadosTabela([]);
      setTotalRegistros(0);
      setProcedimentosDisponiveis([]);
      setProcedimentosSelecionados([]);
      setPaginacao({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      });
      toast.error('❌ Erro de conexão com o servidor.');
    } finally {
      setLoadingTabela(false);
    }
  };

  const handleImprimir = async () => {
    if (!token) {
      console.error('Token de autenticação não encontrado');
      return;
    }

    const erros = {
      unidade: false,
      dataInicio: false,
      dataFim: false
    };

    let temErro = false;
    const camposObrigatorios = [];

    if (!filtros.unidade || !filtros.unidade.value) {
      erros.unidade = true;
      temErro = true;
      camposObrigatorios.push('Unidade');
    }

    if (!filtros.dataInicio) {
      erros.dataInicio = true;
      temErro = true;
      camposObrigatorios.push('Data Início');
    }

    if (!filtros.dataFim) {
      erros.dataFim = true;
      temErro = true;
      camposObrigatorios.push('Data Fim');
    }

    if (temErro) {
      setCamposComErro(erros);
      toast.error(`⚠️ Campos obrigatórios não preenchidos: ${camposObrigatorios.join(', ')}`);
      return;
    }

    if (procedimentosSelecionados.length === 0) {
      toast.error('⚠️ Selecione ao menos um procedimento para imprimir.');
      return;
    }

    setCamposComErro(erros);
    setLoadingImprimir(true);
    const toastId = toast.loading('⏳ Gerando relatório PDF...');

    try {
      const filtrosSanitizados = {
        ...sanitizarFiltros(filtros),
        procedimentosCodigos: procedimentosSelecionados
      };

      const result = await apiService.authenticatedRequest('/api/bpa/relatorio-procedimentos', {
        method: 'POST',
        body: JSON.stringify(filtrosSanitizados)
      });

      if (result.success && result.data?.arquivoBase64) {
        const byteCharacters = atob(result.data.arquivoBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.data.nomeArquivo || 'relatorio_procedimentos.pdf';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 100);

        toast.success('✅ Relatório PDF gerado com sucesso!', { id: toastId });
      } else {
        toast.error(`❌ Erro ao gerar relatório: ${result.error || 'Arquivo não disponível'}`, {
          id: toastId
        });
      }
    } catch (error) {
      console.error('Erro ao gerar relatório PDF:', error);
      toast.error('❌ Erro de conexão com o servidor.', { id: toastId });
    } finally {
      setLoadingImprimir(false);
    }
  };

  const handleToggleProcedimento = (codigo) => {
    setProcedimentosSelecionados((prev) => {
      const next = prev.includes(codigo)
        ? prev.filter((c) => c !== codigo)
        : [...prev, codigo];
      sincronizarFiltroProcedimento(next);
      return next;
    });
  };

  const sincronizarFiltroProcedimento = (codigos) => {
    const options = procedimentosDisponiveis.length > 0
      ? procedimentosDisponiveis
          .filter((p) => codigos.includes(p.codigo))
          .map((p) => ({
            value: p.codigo,
            label: `${p.codigo} — ${p.descricao}`
          }))
      : (Array.isArray(filtros.procedimento) ? filtros.procedimento : [])
          .filter((opt) => codigos.includes(String(opt.value)));

    // Completar com opções já no filtro se não estiverem na lista distinta
    const byCode = new Map(options.map((o) => [o.value, o]));
    for (const codigo of codigos) {
      if (!byCode.has(codigo)) {
        const fromFilter = (Array.isArray(filtros.procedimento) ? filtros.procedimento : [])
          .find((opt) => String(opt.value) === codigo);
        byCode.set(codigo, fromFilter || { value: codigo, label: codigo });
      }
    }

    setFiltros((prev) => ({
      ...prev,
      procedimento: Array.from(byCode.values())
    }));
  };

  const handleMarcarTodosProcedimentos = () => {
    const todos = procedimentosDisponiveis.map((p) => p.codigo);
    setProcedimentosSelecionados(todos);
    sincronizarFiltroProcedimento(todos);
  };

  const handleDesmarcarTodosProcedimentos = () => {
    setProcedimentosSelecionados([]);
    setFiltros((prev) => ({ ...prev, procedimento: [] }));
  };

  const handleGerarBPA = async () => {
    if (!token) {
      console.error('Token de autenticação não encontrado');
      return;
    }

    // Resetar erros
    const erros = {
      unidade: false,
      dataInicio: false,
      dataFim: false
    };

    let temErro = false;
    const camposObrigatorios = [];

    // Validar campo obrigatório: Unidade
    if (!filtros.unidade || !filtros.unidade.value) {
      erros.unidade = true;
      temErro = true;
      camposObrigatorios.push('Unidade');
    }

    // Validar datas obrigatórias
    if (!filtros.dataInicio) {
      erros.dataInicio = true;
      temErro = true;
      camposObrigatorios.push('Data Início');
    }

    if (!filtros.dataFim) {
      erros.dataFim = true;
      temErro = true;
      camposObrigatorios.push('Data Fim');
    }

    // Se houver erros, mostrar toast e marcar campos
    if (temErro) {
      setCamposComErro(erros);
      toast.error(`⚠️ Campos obrigatórios não preenchidos: ${camposObrigatorios.join(', ')}`);
      return;
    }

    // Validar se há dados para gerar BPA
    if (dadosTabela.length === 0 && procedimentosDisponiveis.length === 0) {
      toast.error('⚠️ Não há dados para gerar o BPA. Clique em "Buscar" primeiro.');
      return;
    }

    if (procedimentosSelecionados.length === 0) {
      toast.error('⚠️ Selecione ao menos um procedimento para gerar o BPA.');
      return;
    }

    // Limpar marcações de erro
    setCamposComErro(erros);

    try {
      const filtrosSanitizados = {
        ...sanitizarFiltros(filtros),
        procedimentosCodigos: procedimentosSelecionados
      };
      
      toast.loading('⏳ Gerando BPA...');

      const result = await apiService.authenticatedRequest('/api/bpa/gerar', {
        method: 'POST',
        body: JSON.stringify(filtrosSanitizados)
      });

      if (result.success) {
        toast.success(`✅ BPA gerado com sucesso! ${result.data.total_registros} registros processados.`);
        // Limpar dados da tabela após gerar BPA
        setDadosTabela([]);
        setTotalRegistros(0);
        setProcedimentosDisponiveis([]);
        setProcedimentosSelecionados([]);
      } else {
        toast.error(`❌ Erro ao gerar BPA: ${result.error}`);
      }
    } catch (error) {
      console.error('Erro ao gerar BPA:', error);
      toast.error('❌ Erro de conexão com o servidor.');
    }
  };

  const handleLimpar = () => {
    setFiltros({
      unidade: null,
      profissional: null,
      procedimento: [],
      categoria: null,
      dataInicio: getPreviousMonthDates().dataInicio,
      dataFim: getPreviousMonthDates().dataFim,
      tipoAtendimento: 'ambulatorio',
      mostrarDadosBPA: false
    });
    setDadosTabela([]);
    setTotalRegistros(0);
    setProcedimentosDisponiveis([]);
    setProcedimentosSelecionados([]);
    setPaginacao({
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false
    });
    // Limpar erros
    setCamposComErro({
      unidade: false,
      dataInicio: false,
      dataFim: false
    });
    toast('🗑️ Formulário limpo');
  };

  // Funções de paginação
  const handlePaginaAnterior = () => {
    if (paginacao.hasPrev) {
      handleBuscar(paginacao.page - 1);
    }
  };

  const handleProximaPagina = () => {
    if (paginacao.hasNext) {
      handleBuscar(paginacao.page + 1);
    }
  };

  const handleIrParaPagina = (pagina) => {
    if (pagina >= 1 && pagina <= paginacao.totalPages) {
      handleBuscar(pagina);
    }
  };

  return (
    <div className="producao-bpa">
      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#333',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            padding: '16px 20px',
            fontSize: '15px',
            fontWeight: '500',
            maxWidth: '500px',
            minWidth: '300px'
          },
          success: {
            iconTheme: {
              primary: '#48bb78',
              secondary: '#fff',
            },
            style: {
              borderLeft: '4px solid #48bb78',
            },
          },
          error: {
            iconTheme: {
              primary: '#e53e3e',
              secondary: '#fff',
            },
            style: {
              borderLeft: '4px solid #e53e3e',
            },
          },
          loading: {
            iconTheme: {
              primary: '#4299e1',
              secondary: '#fff',
            },
            style: {
              borderLeft: '4px solid #4299e1',
            },
          },
        }}
      />
      <div className="page-header">
        <h1>BPA - Boletim de Produção Ambulatorial</h1>
      </div>

      <div className="filtros-section">
        <div className="filtros-grid">
          <div className="filtro-group">
            <label>Unidade (*)</label>
            <Select
              value={filtros.unidade}
              onChange={(option) => handleInputChange('unidade', option)}
              options={opcoesUnidade}
              placeholder={loading.unidade ? "Carregando unidades..." : "Selecionar unidade..."}
              isSearchable
              isClearable
              isLoading={loading.unidade}
              className={`react-select-container ${camposComErro.unidade ? 'campo-erro' : ''}`}
              classNamePrefix="react-select"
            />
          </div>

          <div className="filtro-group">
            <label>Profissional</label>
            <Select
              value={filtros.profissional}
              onChange={(option) => handleInputChange('profissional', option)}
              options={opcoesProfissional}
              placeholder={loading.profissional ? "Carregando profissionais..." : "Selecionar profissional..."}
              isSearchable
              isClearable
              isLoading={loading.profissional}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>

          <div className="filtro-group filtro-procedimento-multi">
            <label>Procedimentos (múltipla escolha)</label>
            <Select
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              controlShouldRenderValue
              components={procedimentoSelectComponents}
              value={Array.isArray(filtros.procedimento) ? filtros.procedimento : []}
              onChange={(options) => handleInputChange('procedimento', options || [])}
              options={opcoesProcedimento}
              placeholder={loading.procedimento ? "Carregando procedimentos..." : "Selecione um ou mais procedimentos..."}
              isSearchable
              isClearable
              isLoading={loading.procedimento}
              className="react-select-container"
              classNamePrefix="react-select"
              noOptionsMessage={() => 'Nenhum procedimento encontrado'}
            />
          </div>

          <div className="filtro-group">
            <label>Categoria</label>
            <Select
              value={filtros.categoria}
              onChange={(option) => handleInputChange('categoria', option)}
              options={opcoesCategoria}
              placeholder={loading.categoria ? "Carregando categorias..." : "Selecionar categoria..."}
              isSearchable
              isClearable
              isLoading={loading.categoria}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>

          <div className="linha-filtros-superior">
            <div className="linha-opcoes">
              <div className="filtro-group">
                <label>Data Início: (*)</label>
                <div className={`date-wrapper ${camposComErro.dataInicio ? 'campo-erro' : ''}`}>
                  <DatePicker
                    selected={filtros.dataInicio}
                    onChange={(date) => handleInputChange('dataInicio', date)}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="DD/MM/AAAA"
                    className="date-picker-input"
                    showPopperArrow={false}
                    locale="pt-BR"
                    months={[
                      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
                    ]}
                    weekdays={['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']}
                  />
                </div>
              </div>

              <div className="filtro-group">
                <label>Data Fim: (*)</label>
                <div className={`date-wrapper ${camposComErro.dataFim ? 'campo-erro' : ''}`}>
                  <DatePicker
                    selected={filtros.dataFim}
                    onChange={(date) => handleInputChange('dataFim', date)}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="DD/MM/AAAA"
                    className="date-picker-input"
                    showPopperArrow={false}
                    locale="pt-BR"
                    months={[
                      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
                    ]}
                    weekdays={['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']}
                  />
                </div>
              </div>

              <div className="tipo-buttons tipo-buttons-inline">
                <button 
                  className={`tipo-btn ${filtros.tipoAtendimento === 'ambulatorio' ? 'active' : ''}`}
                  onClick={() => handleTipoAtendimento('ambulatorio')}
                >
                  Ambulatório
                </button>
                <button 
                  className={`tipo-btn ${filtros.tipoAtendimento === 'odontologia' ? 'active' : ''}`}
                  onClick={() => handleTipoAtendimento('odontologia')}
                >
                  Odontologia
                </button>
              </div>
            </div>

            <div className="linha-checkbox">
              <div className="checkbox-wrapper">
                <input
                  type="checkbox"
                  id="mostrarDadosBPA"
                  checked={filtros.mostrarDadosBPA}
                  onChange={(e) => handleInputChange('mostrarDadosBPA', e.target.checked)}
                  className="checkbox-input"
                />
                <label htmlFor="mostrarDadosBPA" className="checkbox-label">
                  <span className="checkbox-text">Mostrar dados BPA</span>
                  <span className="checkbox-description">Exibir exatamente os dados que vão para o arquivo TXT</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="action-buttons">
          <button className="action-btn buscar" onClick={handleBuscar}>
            <Search size={16} />
            Buscar
          </button>
          <button
            className="action-btn imprimir"
            onClick={handleImprimir}
            disabled={loadingImprimir}
          >
            <Printer size={16} />
            {loadingImprimir ? 'Gerando PDF...' : 'Imprimir'}
          </button>
          <button className="action-btn gerar" onClick={handleGerarBPA}>
            <FileText size={16} />
            Gerar BPA
          </button>
          <button className="action-btn limpar" onClick={handleLimpar}>
            <Trash2 size={16} />
            Limpar formulário
          </button>
        </div>
      </div>

      <div className="resultados-section">
        {(procedimentosDisponiveis.length > 0 || loadingProcedimentos) && (
          <div className="selecao-procedimentos">
            <div className="selecao-procedimentos-header">
              <div>
                <h3>Procedimentos para o BPA / Impressão</h3>
                <p>
                  Selecione um ou mais códigos abaixo (não use o filtro de cima para isso).{' '}
                  {procedimentosSelecionados.length} de {procedimentosDisponiveis.length} selecionado
                  {procedimentosDisponiveis.length !== 1 ? 's' : ''}.
                </p>
              </div>
              <div className="selecao-procedimentos-acoes">
                <button
                  type="button"
                  className="selecao-btn"
                  onClick={handleMarcarTodosProcedimentos}
                  disabled={loadingProcedimentos || procedimentosDisponiveis.length === 0}
                >
                  Marcar todos
                </button>
                <button
                  type="button"
                  className="selecao-btn secondary"
                  onClick={handleDesmarcarTodosProcedimentos}
                  disabled={loadingProcedimentos || procedimentosSelecionados.length === 0}
                >
                  Desmarcar todos
                </button>
              </div>
            </div>

            {loadingProcedimentos ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <span>Carregando procedimentos...</span>
              </div>
            ) : (
              <>
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  components={procedimentoSelectComponents}
                  options={procedimentosDisponiveis.map((proc) => ({
                    value: proc.codigo,
                    label: `${proc.codigo} — ${proc.descricao} (${proc.quantidade})`
                  }))}
                  value={procedimentosDisponiveis
                    .filter((proc) => procedimentosSelecionados.includes(proc.codigo))
                    .map((proc) => ({
                      value: proc.codigo,
                      label: `${proc.codigo} — ${proc.descricao} (${proc.quantidade})`
                    }))}
                  onChange={(options) => {
                    const codigos = (options || []).map((opt) => opt.value);
                    setProcedimentosSelecionados(codigos);
                    sincronizarFiltroProcedimento(codigos);
                  }}
                  placeholder="Selecione os procedimentos que entrarão no BPA..."
                  className="react-select-container selecao-procedimentos-select"
                  classNamePrefix="react-select"
                  noOptionsMessage={() => 'Nenhum procedimento encontrado'}
                />
                <div className="selecao-procedimentos-lista">
                  {procedimentosDisponiveis.map((proc) => {
                    const checked = procedimentosSelecionados.includes(proc.codigo);
                    const id = `proc-${proc.codigo}`;
                    return (
                      <label key={proc.codigo} htmlFor={id} className={`selecao-item ${checked ? 'selected' : ''}`}>
                        <input
                          id={id}
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleToggleProcedimento(proc.codigo)}
                        />
                        <span className="selecao-item-texto">
                          <strong>{proc.codigo}</strong> — {proc.descricao}
                        </span>
                        <span className="selecao-item-qtd">{proc.quantidade}</span>
                      </label>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {totalRegistros > 0 && (
          <div className="table-info">
            <span className="total-registros">
              {totalRegistros} registro{totalRegistros !== 1 ? 's' : ''} encontrado{totalRegistros !== 1 ? 's' : ''}
            </span>
          </div>
        )}
        <div className="tabela-container">
          <table className="resultados-tabela">
            <thead>
              <tr>
                <th>Entrada</th>
                <th>CPF</th>
                <th>CNS</th>
                <th>Nome</th>
                <th>DtNasc</th>
                <th>Tipo Ficha</th>
                <th>Tipo Logradouro</th>
                <th>Endereço</th>
                <th>Numero</th>
                <th>Bairro</th>
                <th>CEP</th>
                <th>Município</th>
                <th>Procedimento</th>
              </tr>
            </thead>
            <tbody>
              {loadingTabela ? (
                <tr>
                  <td colSpan="13" style={{ textAlign: 'center', padding: '20px' }}>
                    <div className="loading-spinner">
                      <div className="spinner"></div>
                      <span>Carregando dados...</span>
                    </div>
                  </td>
                </tr>
              ) : dadosTabela.length > 0 ? (
                dadosTabela.map((item, index) => (
                  <tr key={index}>
                    <td>{formatarDataBR(item.entrada)}</td>
                    <td>{item.cpf || 'N/A'}</td>
                    <td>{item.cns || 'N/A'}</td>
                    <td>{item.nome || 'N/A'}</td>
                    <td>{item.dt_nasc ? new Date(item.dt_nasc).toLocaleDateString('pt-BR') : 'N/A'}</td>
                    <td>{item.tipo || 'N/A'}</td>
                    <td>{item.tipo_logradouro || 'N/A'}</td>
                    <td>{item.endereco || 'N/A'}</td>
                    <td>{item.numero || 'N/A'}</td>
                    <td>{item.bairro || 'N/A'}</td>
                    <td>{item.cep || 'N/A'}</td>
                    <td>{item.municipio || 'N/A'}</td>
                    <td>{item.procedimento || 'N/A'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="13" className="no-data">
                    <div>
                      <span>Nenhum registro encontrado</span>
                      <p>Clique em "Buscar" para carregar os dados</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {paginacao.totalPages > 0 && (
          <div className="paginacao">
            <div className="pagination-info">
              <span>
                Mostrando {((paginacao.page - 1) * paginacao.limit) + 1} a {Math.min(paginacao.page * paginacao.limit, paginacao.total)} de {paginacao.total} registros
              </span>
            </div>
            
            <div className="pagination-controls">
              <button 
                className={`pagination-btn ${!paginacao.hasPrev ? 'disabled' : ''}`}
                onClick={handlePaginaAnterior}
                disabled={!paginacao.hasPrev}
              >
                « Anterior
              </button>
              
              <div className="pagination-numbers">
                {(() => {
                  const pages = [];
                  const maxVisible = 5;
                  let startPage = Math.max(1, paginacao.page - Math.floor(maxVisible / 2));
                  let endPage = Math.min(paginacao.totalPages, startPage + maxVisible - 1);
                  
                  if (endPage - startPage + 1 < maxVisible) {
                    startPage = Math.max(1, endPage - maxVisible + 1);
                  }
                  
                  // Primeira página
                  if (startPage > 1) {
                    pages.push(
                      <button key={1} className="page-number" onClick={() => handleIrParaPagina(1)}>
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
                    }
                  }
                  
                  // Páginas do meio
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button 
                        key={i} 
                        className={`page-number ${i === paginacao.page ? 'active' : ''}`}
                        onClick={() => handleIrParaPagina(i)}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  // Última página
                  if (endPage < paginacao.totalPages) {
                    if (endPage < paginacao.totalPages - 1) {
                      pages.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
                    }
                    pages.push(
                      <button 
                        key={paginacao.totalPages} 
                        className="page-number" 
                        onClick={() => handleIrParaPagina(paginacao.totalPages)}
                      >
                        {paginacao.totalPages}
                      </button>
                    );
                  }
                  
                  return pages;
                })()}
              </div>
              
              <button 
                className={`pagination-btn ${!paginacao.hasNext ? 'disabled' : ''}`}
                onClick={handleProximaPagina}
                disabled={!paginacao.hasNext}
              >
                Próxima »
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProducaoBPA;
